var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { FsPaginationBase } from './PaginationBase.js';
/**
 * `FsPaginationArrows` implements the Financial Services implementation of the PaginationArrows
 * component.
 * Be sure to consult with the design guidelines for the PaginationArrows component to ensure proper state
 * usage, it is possible to force the PaginationArrows component into an unsupported state.
 *
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-pagination-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `<fs-pagination-arrows>` call
 *
 * ```javascript
 * import '@cof/fs-pagination-wc/fs-pagination.js';
 * ```
 *
 * To import the `FsPaginationArrows` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsPaginationArrows } from '@cof/fs-pagination-wc';
 * ```
 *
 * ## Anatomy
 * ### DOM Structure
 * ```html
 * <fs-pagination-arrows
 *   accessibility-label="${accessibilityLabel}"
 *   ?compact=${compact}
 *   current-page=${currentPage}
 *   href-template="${hrefTemplate}"
 *   navigation-type="${navigationType}"
 *   page-count=${pageCount}
 *   ?show-first-page-last-page-controls=${showFirstPageLastPageControls}
 * ></fs-pagination-arrows>
 * ```
 *
 * @element fs-pagination-arrows
 * @event change - Dispatched when the user changes the page.
 */
export class FsPaginationArrows extends FsPaginationBase {
    constructor() {
        super(...arguments);
        /**
         * Whether to show the "first page" and "last page" controls along with the "previous" and "next" page controls.
         */
        this.showFirstPageLastPageControls = false;
    }
    /**
     * Render the component.
     *
     * @returns TemplateResult
     */
    render() {
        const constructedClass = `
      fs-pagination
      fs-pagination--arrows-only
      ${this.compact ? 'fs-pagination--compact' : ''}
    `;
        return html `
      <nav aria-label="${this.accessibilityLabel}" class="${constructedClass}" role="navigation">
        <ul class="fs-pagination__list">
          <!-- Render "first" button. -->
          ${this.showFirstPageLastPageControls ? this._renderDirectionControl('first-page', this.currentPage === 1) : ''}

          <!-- Render "previous" button. -->
          ${this._renderDirectionControl('previous-page', this.currentPage === 1)}

          <!-- Render "next" button. -->
          ${this._renderDirectionControl('next-page', this.currentPage === this.pageCount)}

          <!-- Render "last" button. -->
          ${this.showFirstPageLastPageControls
            ? this._renderDirectionControl('last-page', this.currentPage === this.pageCount)
            : ''}
        </ul>
      </nav>
    `;
    }
}
/** The element's canonical name for use with safe define */
FsPaginationArrows.canonicalName = 'pagination-arrows';
__decorate([
    property({ type: Boolean, attribute: 'show-first-page-last-page-controls' })
], FsPaginationArrows.prototype, "showFirstPageLastPageControls", void 0);
//# sourceMappingURL=PaginationArrows.js.map